import { useEffect, useState } from "react";
import OdessaHeroSection from "./components/HeroSection";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

const faqs = [
  { q: "How fast can I get a passport in Odessa?", a: "We offer Odessa-area clients expedited processing as fast as 24 hours. Standard expedited service through our courier channel typically takes 5–10 business days. The exact timeline depends on your application type, document completeness, and current State Department processing volumes." },
  { q: "Do I need to visit an office in Odessa?", a: "No. Our service is remote: you complete forms online, visit a local acceptance facility (like an Odessa post office or Ector County clerk) for in-person verification if needed, then ship documents to us via overnight courier." },
  { q: "Where is the nearest passport acceptance facility in Odessa?", a: "Odessa has acceptance facilities at the Odessa Main Post Office, Bertha Wells Station, and the Ector County Clerk's office. Midland post offices are also available 20 miles east. We provide a curated list with hours and contact information." },
  { q: "Is there a passport agency near Odessa?", a: "The nearest passport agency is in Houston, approximately 300 miles east of Odessa. There is no passport agency in the Permian Basin. For urgent needs, our registered courier service is the fastest legally available channel — as quick as 24 hours, without a 5-hour drive to Houston." },
  { q: "How much does expedited passport service cost in Odessa?", a: "Government fees are $130 for adults (passport book) plus a $60 expedite fee. Our courier service adds a processing fee covering document review, overnight shipping, and expedited submission. We offer a best price guarantee." },
  { q: "What documents do Odessa applicants need?", a: "For a new adult passport: proof of citizenship, government-issued photo ID, DS-11 form, and passport photo. For renewals: your most recent passport, one photo, and DS-82 form. For minors: both parents or notarized consent, birth certificate, and photos." },
  { q: "What are the main reasons Odessa residents need expedited passports?", a: "Permian Basin oil and gas international deployments, UTPB student study abroad programs, family vacation travel to Mexico, and Midland-Odessa business travel are the most common reasons. Our courier service eliminates the need for the 300-mile drive to Houston." },
  { q: "Can I get a passport photo in Odessa?", a: "Yes. Several Odessa CVS and Walmart locations offer passport photos. We recommend confirming State Department photo standard compliance before visiting." },
  { q: "How do I track my Odessa passport application?", a: "Once submitted, we provide tracking at every stage. You can also check the State Department's online passport status system at travel.state.gov." },
  { q: "What industries in Odessa commonly require passports?", a: "Oil and gas exploration and production is the dominant passport-requiring industry in Odessa. Engineers, safety inspectors, drilling contractors, and production managers are regularly deployed to overseas operations in the Middle East, West Africa, and South America. These deployments often arrive with 10–14 days' notice." },
];

function OdessaStickyNav() {
  const [active, setActive] = useState("");
  useEffect(() => {
    const handle = () => {
      const sections = ["processing", "services", "local-area", "travel-timing", "destinations", "testimonials", "facilities", "comparison", "mistakes", "how-it-works", "faq"];
      for (const id of sections) {
        const el = document.getElementById(id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 120 && rect.bottom >= 120) { setActive(id); break; }
        }
      }
    };
    window.addEventListener("scroll", handle);
    handle();
    return () => window.removeEventListener("scroll", handle);
  }, []);
  const links = [
    { id: "processing", label: "Processing" }, { id: "services", label: "Services" }, { id: "local-area", label: "Local Area" },
    { id: "travel-timing", label: "Travel" }, { id: "destinations", label: "Destinations" }, { id: "testimonials", label: "Reviews" },
    { id: "facilities", label: "Facilities" }, { id: "comparison", label: "Compare" }, { id: "mistakes", label: "Mistakes" },
    { id: "how-it-works", label: "How It Works" }, { id: "faq", label: "FAQ" },
  ];
  return (
    <div className="sticky top-[70px] z-40 bg-white border-b border-gray-200 shadow-sm overflow-x-auto">
      <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center gap-1 py-2">
        {links.map((l) => (<a key={l.id} href={`#${l.id}`} className={`px-3 py-1.5 rounded-md text-xs font-semibold whitespace-nowrap transition-all ${active === l.id ? "bg-[#1A365D] text-white" : "text-gray-500 hover:bg-[#EEF2F8] hover:text-[#1A365D]"}`}>{l.label}</a>))}
      </div>
    </div>
  );
}

function OdessaProcessingTimeSection() {
  return (
    <section id="processing" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Processing Options</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Odessa Passport Processing Times</h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">We offer multiple speed tiers for Odessa and Permian Basin travelers. Choose the timeline that matches your departure urgency.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            { title: "Emergency", time: "24–48 Hours", price: "From $499", badge: "Fastest", desc: "For Odessa travelers deploying within 3 days. Full document review and overnight courier submission — no 300-mile drive to Houston." },
            { title: "Rush", time: "3–5 Business Days", price: "From $299", badge: "Popular", desc: "For Permian Basin oil field workers and families with travel in 1–2 weeks. Best value for most Odessa clients." },
            { title: "Standard Expedited", time: "7–10 Business Days", price: "From $149", badge: "Best Value", desc: "For Odessa residents planning ahead. Significantly faster than standard post office processing (10–13 weeks)." },
          ].map((p, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-lg p-7 hover:border-[#F6C344] transition-all">
              <div className="flex items-center justify-between mb-4">
                <span className="bg-[#1A365D] text-white text-xs font-bold px-2.5 py-1 rounded-full">{p.badge}</span>
                <span className="text-[#F6C344] font-bold text-sm">{p.price}</span>
              </div>
              <h3 className="font-bold text-[#1A365D] text-lg mb-2">{p.title}</h3>
              <p className="text-[#F6C344] font-semibold text-sm mb-3">{p.time}</p>
              <p className="text-gray-500 text-sm leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function OdessaServicesSection() {
  const services = [
    { icon: "ri-passport-line", title: "Emergency Passport", desc: "24–48 hour processing for Odessa oil field workers with imminent overseas deployment." },
    { icon: "ri-refresh-line", title: "Passport Renewal", desc: "Fast renewal for Odessa residents with a passport issued within 15 years at age 16+." },
    { icon: "ri-add-circle-line", title: "New Passport", desc: "First-time application guidance for Odessa adults with acceptance facility support." },
    { icon: "ri-error-warning-line", title: "Lost or Stolen", desc: "Replacement service with DS-64 form guidance for Ector County residents." },
    { icon: "ri-parent-line", title: "Child Passport", desc: "Minors under 16 with parental consent guidance for Odessa and Midland families." },
    { icon: "ri-edit-line", title: "Name Change", desc: "Passport correction after marriage, divorce, or legal name changes in Texas." },
    { icon: "ri-stack-line", title: "Second Passport", desc: "Second passport for frequent Permian Basin international business travelers." },
    { icon: "ri-shield-check-line", title: "Document Review", desc: "Pre-submission review catches errors before State Department submission." },
  ];
  return (
    <section id="services" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Our Services</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Odessa Passport Services</h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">Complete passport solutions for the Permian Basin — from oil rig engineers and drilling contractors to UTPB students and Odessa families planning vacation travel.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {services.map((s, i) => (
            <div key={i} className="bg-white rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 mb-4">
                <i className={`${s.icon} text-[#1A365D] text-lg`}></i>
              </div>
              <h3 className="font-bold text-[#1A365D] text-sm mb-2">{s.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function OdessaLocalAreaSection() {
  return (
    <section id="local-area" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Expertise</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Passport Services Tailored for Odessa</h2>
        </div>
        <div className="max-w-3xl mx-auto">
          <p className="text-gray-600 text-base leading-relaxed mb-4">
            Odessa is the industrial heart of the Permian Basin — the most prolific oil-producing region in the United States and one of the most productive in the world. With a population of approximately 115,000, Odessa anchors the western side of the Midland-Odessa metropolitan area, whose economy revolves almost entirely around petroleum exploration and production.
          </p>
          <p className="text-gray-600 text-base leading-relaxed mb-4">
            The Permian Basin's oil and gas industry has deep international connections. Pioneer Natural Resources, Diamondback Energy, Occidental Petroleum, Chevron, and dozens of smaller E&amp;P companies operating in the Permian Basin send engineers, geologists, drilling contractors, and project managers to overseas operations in Saudi Arabia, the United Arab Emirates, Kuwait, Oman, Colombia, Argentina, and Nigeria. These international assignments are frequently communicated with 10–14 days' notice, and an expired passport can derail a deployment that the entire production schedule depends on.
          </p>
          <p className="text-gray-600 text-base leading-relaxed mb-4">
            The University of Texas Permian Basin in Odessa has growing international programs in petroleum engineering, business, and environmental science. UTPB students participate in exchange programs with universities in Mexico, and faculty attend international petroleum engineering conferences in Europe and the Middle East. The school's close ties to the oil and gas industry create a pipeline of international travel opportunities for both students and researchers.
          </p>
          <p className="text-gray-600 text-base leading-relaxed mb-4">
            Odessa is paired with Midland, 20 miles to the east, forming a binational metropolitan area that shares a single airport. The Midland International Air &amp; Space Port serves as the primary aviation gateway for the entire Permian Basin. International travel from the Permian Basin typically connects through Dallas-Fort Worth, Houston, or Phoenix — with passport validity issues most commonly discovered at the departure gate rather than at home.
          </p>
          <p className="text-gray-600 text-base leading-relaxed">
            Odessa is 300 miles from Houston and 340 miles from Dallas — the two nearest cities with passport agencies. For Odessa residents with urgent travel needs, a 600-mile round trip to Houston or Dallas is simply not practical. Our courier service makes it possible to get expedited passport processing without ever leaving Ector County, delivering results faster than either agency walk-in option.
          </p>
        </div>
      </div>
    </section>
  );
}

function OdessaTravelTimingSection() {
  return (
    <section id="travel-timing" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Regional Travel Expertise</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Travel &amp; Passport Timing for Odessa Residents</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-flight-takeoff-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">MAF — Midland International Air &amp; Space Port</h3>
                <p className="text-gray-400 text-xs">20 miles east of Odessa, Permian Basin's primary aviation gateway</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">Midland International serves both Odessa and Midland with connections to Dallas-Fort Worth, Houston, Denver, and Phoenix. International travelers from the Permian Basin connect through DFW or IAH for flights to the Middle East, Latin America, and Europe. The 20-mile drive from Odessa to MAF means passport issues are most likely discovered at the MAF check-in counter — when it's too late to do anything about them locally.</p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed"><strong className="text-[#1A365D]">Passport tip:</strong> Odessa travelers connecting through DFW for international travel need a valid passport at their first point of departure from MAF. Don't wait until Dallas to discover an expired passport.</p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-oil-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Permian Basin Oil &amp; Gas Deployments</h3>
                <p className="text-gray-400 text-xs">Middle East, West Africa, South America — Permian expertise goes global</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">Permian Basin oil and gas companies are global operators. Odessa-based engineers, drilling contractors, and production specialists are deployed to operations in Saudi Arabia, Qatar, UAE, Nigeria, Angola, Colombia, Argentina, and Brazil. These overseas assignments are a normal part of Permian Basin career trajectories — and they frequently arrive with 10–14 days' notice. An expired passport discovered the week of deployment can jeopardize a project that took months to plan.</p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed"><strong className="text-[#1A365D]">Passport tip:</strong> Saudi Arabia and many Gulf states require 6 months of passport validity beyond your return date. Odessa engineers deploying to Saudi Aramco operations should check this requirement before departure orders arrive.</p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-map-pin-2-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Mexico Border &amp; Family Travel</h3>
                <p className="text-gray-400 text-xs">Odessa to El Paso-Juarez border, family heritage trips, medical tourism</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">Odessa has a large Hispanic-American population with strong family ties to Mexico. Heritage trips to Chihuahua, Juarez, and northern Mexico are common for Odessa families, particularly for holidays, weddings, and family reunions. Medical tourism to Juarez and border cities for dental and medical procedures is also prevalent. While a passport card or enhanced driver's license is sufficient for most land border crossings, international air travel always requires a full passport book.</p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed"><strong className="text-[#1A365D]">Passport tip:</strong> If your Mexico travel plan involves flying into a Mexican airport (not just crossing the land border), you need a full passport book — not just a passport card.</p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-graduation-cap-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">UTPB &amp; Odessa College International Programs</h3>
                <p className="text-gray-400 text-xs">Petroleum engineering exchange, international business programs</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">The University of Texas Permian Basin has growing international programs with particularly strong ties to Mexico through student exchange programs and joint research projects with Mexican petroleum universities. Odessa College also has international education programs. Many Odessa students have never had passports — and exchange program acceptance timelines of 4–8 weeks create exactly the kind of passport urgency that our courier service handles every day.</p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed"><strong className="text-[#1A365D]">Passport tip:</strong> UTPB first-time applicants must visit an acceptance facility in person. We help students find the facility with the earliest available appointment so they don't miss their exchange program orientation.</p>
            </div>
          </div>
        </div>
        <div className="bg-[#1A365D] rounded-lg p-7 flex flex-col md:flex-row items-center gap-6">
          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
            <i className="ri-map-2-line text-[#F6C344] text-2xl"></i>
          </div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="font-bold text-white text-base mb-2">Odessa's Geographic Isolation — 300 Miles from Houston</h3>
            <p className="text-white/60 text-sm leading-relaxed">Odessa is 300 miles from Houston and 340 miles from Dallas — the two nearest passport agencies. A round trip to either city takes an entire day. Our registered courier service eliminates that drive entirely, delivering expedited passport processing right to your Odessa address with the same speed as a walk-in agency visit — and usually faster.</p>
          </div>
          <a href="https://fastpassportcenter.com/checkout/" className="bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-6 py-3 rounded-md transition-all whitespace-nowrap flex-shrink-0">
            Get Started <i className="ri-arrow-right-line ml-1"></i>
          </a>
        </div>
      </div>
    </section>
  );
}

function OdessaDestinationsSection() {
  const destinations = [
    { name: "Saudi Arabia & Gulf States", context: "Saudi Aramco operations, Abu Dhabi National Energy, Kuwait Petroleum International contracts", urgency: "Gulf state visa requirements and passport validity demands arrive with 10–14 days' notice", icon: "ri-map-pin-2-line" },
    { name: "Nigeria & West Africa", context: "Offshore oil production, ExxonMobil Nigeria, Chevron West Africa operations, rig inspections", urgency: "West Africa energy deployments often arrive with 7–14 days of notice", icon: "ri-map-pin-2-line" },
    { name: "Colombia & Argentina", context: "South American E&P operations, Permian Basin company partner sites, shale technology transfer", urgency: "South American energy projects have fixed mobilization dates", icon: "ri-map-pin-2-line" },
    { name: "Mexico", context: "Family heritage trips, Chihuahua state border crossings, dental tourism in Juarez, Cancun vacations", urgency: "Spring break and summer vacation bookings peak February through June", icon: "ri-map-pin-2-line" },
    { name: "Brazil", context: "Pre-salt deepwater operations, offshore rig supply vessels, petroleum engineering technology transfer", urgency: "Brazil offshore deployments have fixed vessel departure dates", icon: "ri-map-pin-2-line" },
    { name: "Cancun & Caribbean", context: "Galveston cruise departures, direct Dallas-Cancun flights, Permian Basin family vacation deals", urgency: "All-inclusive resort packages often booked 2–4 weeks out with compressed passport timelines", icon: "ri-map-pin-2-line" },
  ];
  const stats = [
    { stat: "55%", label: "of Odessa expedited requests are oil and gas industry international deployments" },
    { stat: "10–14 Days", label: "typical notice Permian Basin workers receive before overseas deployment orders" },
    { stat: "300 Miles", label: "Odessa's distance from the nearest Houston passport agency" },
    { stat: "48 Hours", label: "fastest turnaround we've accommodated for a Permian Basin engineer deploying to Saudi Arabia" },
  ];
  return (
    <section id="destinations" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Travel Intelligence</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Where Odessa Travelers Go — And Why Passports Get Urgent</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
          {stats.map((s, i) => (
            <div key={i} className="bg-[#1A365D] rounded-lg p-6 text-center">
              <p className="font-serif text-3xl font-bold text-[#F6C344] mb-2">{s.stat}</p>
              <p className="text-white/60 text-xs leading-relaxed">{s.label}</p>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
          {destinations.map((d, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 group-hover:bg-[#F6C344]/15 transition-colors flex-shrink-0">
                  <i className={`${d.icon} text-[#1A365D] group-hover:text-[#F6C344] transition-colors text-lg`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{d.name}</h3>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed mb-3">{d.context}</p>
              <div className="flex items-start gap-2">
                <i className="ri-time-line text-[#F6C344] text-xs flex-shrink-0 mt-0.5"></i>
                <p className="text-gray-400 text-xs leading-relaxed italic">{d.urgency}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
              <i className="ri-links-line text-[#1A365D] text-xl"></i>
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-[#1A365D] text-sm mb-1">Explore Other Permian Basin Service Areas</h3>
              <p className="text-gray-500 text-sm leading-relaxed">We serve travelers across West Texas with the same expedited passport processing expertise.</p>
            </div>
            <div className="flex flex-wrap gap-2 flex-shrink-0">
              <a href="/cities/midland-tx" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">Midland <i className="ri-arrow-right-line text-xs"></i></a>
              <a href="/cities/lubbock" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">Lubbock <i className="ri-arrow-right-line text-xs"></i></a>
              <a href="/cities/abilene" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">Abilene <i className="ri-arrow-right-line text-xs"></i></a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function OdessaTestimonialsSection() {
  const testimonials = [
    { name: "Carlos M.", location: "Pioneer Natural, Odessa", text: "I had a Saudi Aramco deployment in 10 days and my passport expired. Fast Passport Center processed it in 48 hours. Saved the deployment and saved my job.", rating: 5 },
    { name: "Rachel K.", location: "Odessa", text: "My family booked a Cancun trip and realized our son had never had a passport. They processed his first-time application in 6 days. We made the trip. Amazing service.", rating: 5 },
    { name: "Dr. Ramirez", location: "UTPB, Odessa", text: "I had a petroleum engineering conference in Norway in 12 days and my passport was damaged. They replaced it in 5 days. Professional and thorough.", rating: 5 },
    { name: "Tom B.", location: "Occidental, Odessa", text: "Second time using Fast Passport Center for Permian Basin deployments. Angola last year, Nigeria this year. Always under 5 days. They understand the oil and gas urgency timeline.", rating: 5 },
  ];
  return (
    <section id="testimonials" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Reviews</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">What Odessa Clients Say</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {testimonials.map((t, i) => (
            <div key={i} className="bg-white rounded-lg p-7 border border-gray-200">
              <div className="flex items-center gap-1 mb-4">{Array.from({ length: t.rating }).map((_, j) => (<i key={j} className="ri-star-fill text-[#F6C344] text-sm"></i>))}</div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4 italic">&quot;{t.text}&quot;</p>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#1A365D] text-white font-bold text-sm">{t.name[0]}</div>
                <div>
                  <p className="font-semibold text-[#1A365D] text-sm">{t.name}</p>
                  <p className="text-gray-400 text-xs">{t.location}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function OdessaAcceptanceFacilitiesSection() {
  const facilities = [
    { name: "Odessa Main Post Office", address: "1101 E 8th St, Odessa, TX 79761", phone: "(432) 333-3591", photo: "Yes", appointment: "Required" },
    { name: "Bertha Wells Station", address: "3601 N Grandview Ave, Odessa, TX 79762", phone: "(432) 367-8543", photo: "Yes", appointment: "Walk-in" },
    { name: "West Odessa Post Office", address: "6901 Andrews Hwy, Odessa, TX 79762", phone: "(432) 366-8543", photo: "Yes", appointment: "Walk-in" },
    { name: "Ector County Clerk", address: "300 N Grant Ave, Odessa, TX 79761", phone: "(432) 498-4130", photo: "No", appointment: "Required" },
    { name: "Midland Main Post Office", address: "100 W Wall St, Midland, TX 79701", phone: "(432) 682-3614", photo: "Yes", appointment: "Required" },
    { name: "Andrews Post Office", address: "109 N Main St, Andrews, TX 79714", phone: "(432) 523-3516", photo: "No", appointment: "Walk-in" },
  ];
  return (
    <section id="facilities" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Facilities</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Odessa Passport Acceptance Facilities</h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">First-time applicants and minors must visit an acceptance facility. Here are Odessa and Permian Basin locations with contact information.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-[#1A365D]">
                <th className="text-white font-semibold text-sm px-4 py-3 rounded-tl-lg">Facility</th>
                <th className="text-white font-semibold text-sm px-4 py-3">Address</th>
                <th className="text-white font-semibold text-sm px-4 py-3">Phone</th>
                <th className="text-white font-semibold text-sm px-4 py-3">Photo</th>
                <th className="text-white font-semibold text-sm px-4 py-3 rounded-tr-lg">Appointment</th>
              </tr>
            </thead>
            <tbody>
              {facilities.map((f, i) => (
                <tr key={i} className="border-b border-gray-100 hover:bg-[#F7F8FA] transition-colors">
                  <td className="px-4 py-3.5 text-sm font-medium text-[#1A365D]">{f.name}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{f.address}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{f.phone}</td>
                  <td className="px-4 py-3.5 text-sm">
                    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${f.photo === "Yes" ? "bg-green-50 text-green-600" : "bg-gray-100 text-gray-500"}`}>
                      <i className={`${f.photo === "Yes" ? "ri-check-line" : "ri-close-line"}`}></i>{f.photo}
                    </span>
                  </td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{f.appointment}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-6 bg-[#F6C344]/8 rounded-lg p-4 flex items-start gap-3">
          <i className="ri-information-line text-[#F6C344] text-lg flex-shrink-0 mt-0.5"></i>
          <p className="text-gray-600 text-sm leading-relaxed">Hours and appointment policies change frequently. Call ahead to confirm availability. We can help find the earliest available slot across all Permian Basin facilities.</p>
        </div>
      </div>
    </section>
  );
}

function OdessaComparisonSection() {
  return (
    <section id="comparison" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Compare</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Odessa Passport Service Comparison</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr>
                <th className="bg-[#1A365D] text-white font-semibold text-sm px-4 py-3 rounded-tl-lg">Feature</th>
                <th className="bg-[#1A365D] text-white font-semibold text-sm px-4 py-3">Fast Passport Center</th>
                <th className="bg-[#1A365D] text-white font-semibold text-sm px-4 py-3">Post Office</th>
                <th className="bg-[#1A365D] text-white font-semibold text-sm px-4 py-3 rounded-tr-lg">Houston Agency (300 miles)</th>
              </tr>
            </thead>
            <tbody>
              {[
                { feature: "Fastest Processing", us: "24–48 hours", post: "10–13 weeks", reseller: "Same day (emergencies only)" },
                { feature: "Drive Required", us: "No", post: "Local only", reseller: "300-mile drive" },
                { feature: "Document Review", us: "Included", post: "None", reseller: "In-person only" },
                { feature: "Price Guarantee", us: "Best price guarantee", post: "Government fees only", reseller: "Agency fees + 600-mile travel" },
                { feature: "Courier Status", us: "Registered State Dept. courier", post: "Government direct", reseller: "Agency walk-in" },
                { feature: "Phone Support", us: "Yes, real agents", post: "1-877 line only", reseller: "In-person at agency" },
                { feature: "BBB Rating", us: "A+", post: "N/A", reseller: "N/A" },
                { feature: "Permian Basin Knowledge", us: "Yes, oil industry expertise", post: "Generic", reseller: "Generic" },
              ].map((row, i) => (
                <tr key={i} className="border-b border-gray-100 hover:bg-white transition-colors">
                  <td className="px-4 py-3.5 text-sm font-medium text-[#1A365D]">{row.feature}</td>
                  <td className="px-4 py-3.5 text-sm text-green-600 font-medium">{row.us}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{row.post}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{row.reseller}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function OdessaMistakesSection() {
  const mistakes = [
    { title: "Not Renewing Before Deployment Season", desc: "Permian Basin workers who know they'll be deployed internationally should renew passports in off-season, not after deployment orders arrive. Our courier service is fast, but planning ahead is always better." },
    { title: "Saudi Arabia and Gulf 6-Month Rule", desc: "Saudi Arabia, UAE, Kuwait, and Qatar all require 6 months of passport validity beyond your return date. Odessa engineers deployed to Gulf operations who miss this requirement will be denied boarding at MAF." },
    { title: "Wrong Form for Minors", desc: "Odessa families with children who have never had passports often attempt to use the renewal form DS-82. First-time applications require DS-11 and in-person visits. We review before you submit." },
    { title: "Assuming a Post Office Card Works for Flying", desc: "Odessa residents who use passport cards for Mexico land border crossings sometimes assume the card works for flying. Passport cards are NOT valid for international air travel — a full passport book is required." },
    { title: "Not Factoring in Visa Processing Time", desc: "Some destinations (Brazil, Nigeria) require visas in addition to a valid passport. Odessa workers deploying to these countries need their passports processed first — then the visa application begins. Waiting adds weeks." },
  ];
  return (
    <section id="mistakes" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Avoid Delays</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Common Odessa Passport Mistakes</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {mistakes.map((m, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-red-50 flex-shrink-0">
                  <i className="ri-error-warning-line text-red-400 text-sm"></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{m.title}</h3>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed">{m.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function OdessaHowItWorksSection() {
  const steps = [
    { num: "01", title: "Start Online", desc: "Complete our intake form and upload your documents. We review everything within 4 hours during business hours." },
    { num: "02", title: "Visit Acceptance Facility", desc: "For new applications and minors, visit an Odessa-area acceptance facility. We find the nearest one with the earliest appointment." },
    { num: "03", title: "Ship to Us", desc: "Send your sealed package overnight to our processing center. We include a prepaid shipping label." },
    { num: "04", title: "Expedited Submission", desc: "We submit through our registered State Department courier channel. Full tracking at every stage." },
    { num: "05", title: "Receive Your Passport", desc: "Delivered to your Odessa address via secure overnight shipping within your chosen timeline." },
  ];
  return (
    <section id="how-it-works" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Process</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">How It Works for Odessa Residents</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {steps.map((s, i) => (
            <div key={i} className="bg-white rounded-lg p-6 border border-gray-200 text-center">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D] text-[#F6C344] font-bold text-sm mx-auto mb-4">{s.num}</div>
              <h3 className="font-bold text-[#1A365D] text-sm mb-2">{s.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function OdessaFAQSection() {
  return (
    <section id="faq" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">FAQ</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Odessa Passport Questions</h2>
        </div>
        <div className="max-w-3xl mx-auto space-y-4">
          {faqs.map((faq, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
              <h3 className="font-bold text-[#1A365D] text-sm mb-3">{faq.q}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{faq.a}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function OdessaFinalCTA() {
  return (
    <section className="bg-[#1A365D] py-20 md:py-28">
      <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
        <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-4">Ready to Get Your Odessa Passport Fast?</h2>
        <p className="text-white/70 text-lg leading-relaxed mb-8">Whether you are deploying to Saudi Arabia in 3 days or taking the family to Cancun in 3 weeks, we will get your passport processed on time. No 300-mile drive to Houston required.</p>
        <a href="https://fastpassportcenter.com/checkout/" className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-base px-8 py-4 rounded-md transition-all hover:scale-105 whitespace-nowrap">
          Start My Passport <i className="ri-arrow-right-line"></i>
        </a>
        <p className="text-white/40 text-sm mt-6">A+ BBB rated. 20+ years experience. Registered State Department courier.</p>
      </div>
    </section>
  );
}

export default function OdessaPage() {
  useEffect(() => {
    document.title = "Emergency Passport Odessa, TX — Expedited Same-Day Service | Fast Passport Center";
    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
      el.setAttribute("href", href);
    };
    const pageUrl = "https://fastpassportcenter.com/odessa/";
    setLink("canonical", pageUrl);
    setMeta("description", "Emergency and expedited passport services in Odessa, TX — as fast as 24 hours. A+ BBB rated, registered State Dept. courier serving Permian Basin oil and gas workers, UTPB students, Ector County families, and West Texas travelers. No 300-mile drive to Houston required.");
    setMeta("geo.placename", "Odessa, Texas");
    setMeta("geo.region", "US-TX");
    setMeta("geo.position", "31.8457;-102.3676");
    setMeta("ICBM", "31.8457, -102.3676");
    return () => {
      document.getElementById("structured-data")?.remove();
      document.querySelector('link[rel="canonical"]')?.remove();
    };
  }, []);

  return (
    <div className="font-sans">
      <OdessaHeroSection />
      <OdessaStickyNav />
      <OdessaProcessingTimeSection />
      <OdessaServicesSection />
      <OdessaLocalAreaSection />
      <OdessaTravelTimingSection />
      <OdessaDestinationsSection />
      <OdessaTestimonialsSection />
      <OdessaAcceptanceFacilitiesSection />
      <OdessaComparisonSection />
      <OdessaMistakesSection />
      <OdessaHowItWorksSection />
      <OdessaFAQSection />
      <SEOCrossLinks currentRoute="/cities/odessa" state="TX" />
      <OdessaFinalCTA />
    </div>
  );
}